/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../theme';
import { 
  PlusCircle, 
  ArrowLeft,
  Save,
  Image as ImageIcon,
  MapPin,
  Tag,
  DollarSign,
  FileText
} from 'lucide-react';
import { ROUTES } from '../routes';
import { itemService } from '../services/itemService';

const AddItem: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const theme = useTheme();
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    categoryId: '',
    location: ''
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await itemService.getCategories();
        if (result.data && result.data.data) {
          setCategories(result.data.data);
          if (result.data.data.length > 0 && !isEditing) {
            setFormData(prev => ({ ...prev, categoryId: result.data.data[0].id.toString() }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };

    const fetchItem = async () => {
      if (!isEditing) return;
      try {
        const result = await itemService.getItemDetails(id!);
        if (result.data && result.data.data) {
          const item = result.data.data;
          setFormData({
            title: item.title,
            description: item.description,
            price: item.price.toString(),
            categoryId: item.categoryId.toString(),
            location: item.location || ''
          });
        }
      } catch (err) {
        console.error("Failed to fetch item", err);
        setError("Failed to load item for editing.");
      }
    };

    fetchCategories();
    fetchItem();
  }, [id, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = isEditing 
        ? await itemService.updateItem(id!, {
            ...formData,
            price: parseFloat(formData.price),
            categoryId: parseInt(formData.categoryId)
          })
        : await itemService.createItem({
            ...formData,
            price: parseFloat(formData.price),
            categoryId: parseInt(formData.categoryId)
          });

      if (response.status === 200 || response.status === 201) {
        navigate(ROUTES.LISTINGS);
      } else {
        setError(response.data.error?.description || `Failed to ${isEditing ? 'update' : 'create'} item.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.description || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl border hover:bg-gray-50 transition-all flex items-center gap-2 font-bold text-sm"
          style={{ borderColor: theme.colors.border, color: theme.colors.mutedText }}
        >
          <ArrowLeft size={18} />
          Back
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>
            {isEditing ? 'Edit Listing' : 'Create New Listing'}
          </h1>
          <p style={{ color: theme.colors.mutedText }}>
            {isEditing ? 'Update your item details and pricing.' : 'List a new item for sale in the marketplace.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="p-8 rounded-3xl border shadow-sm space-y-6" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            
            {error && (
              <div className="p-4 rounded-xl border text-sm font-bold" 
                   style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                  <Tag size={14} /> Title
                </label>
                <input
                  type="text"
                  name="title"
                  required
                  placeholder="What are you selling?"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                    <DollarSign size={14} /> Price ($)
                  </label>
                  <input
                    type="number"
                    name="price"
                    step="0.01"
                    required
                    placeholder="0.00"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                    Category
                  </label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all appearance-none"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                  <FileText size={14} /> Description
                </label>
                <textarea
                  name="description"
                  required
                  rows={6}
                  placeholder="Describe your item in detail (condition, age, etc.)"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                  <MapPin size={14} /> Location
                </label>
                <input
                  type="text"
                  name="location"
                  placeholder="City, State"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex-1 py-4 px-6 rounded-2xl border font-bold transition-all hover:bg-gray-50"
                  style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-4 px-6 rounded-2xl font-black text-white transition-all hover:opacity-90 shadow-xl disabled:opacity-50"
                  style={{ backgroundColor: theme.colors.primary }}
                >
                  <div className="flex items-center justify-center gap-2">
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : isEditing ? (
                      <Save size={20} />
                    ) : (
                      <PlusCircle size={20} />
                    )}
                    {loading ? 'Processing...' : isEditing ? 'Update Listing' : 'Publish Listing'}
                  </div>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar / Tips */}
        <div className="space-y-6">
          <div className="p-6 rounded-3xl border shadow-sm space-y-4" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: theme.colors.primary + '15' }}>
              <ImageIcon size={24} style={{ color: theme.colors.primary }} />
            </div>
            <h3 className="font-bold" style={{ color: theme.colors.text }}>Add Photos</h3>
            <p className="text-sm" style={{ color: theme.colors.mutedText }}>
              Items with clear, bright photos sell 3x faster. You can add up to 10 photos of your item.
            </p>
            <button className="w-full py-3 rounded-xl border-2 border-dashed font-bold text-sm transition-all hover:bg-gray-50"
                    style={{ borderColor: theme.colors.border, color: theme.colors.mutedText }}>
              Upload Photos
            </button>
          </div>

          <div className="p-6 rounded-3xl border shadow-sm space-y-4" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <h3 className="font-bold" style={{ color: theme.colors.text }}>Quick Tips</h3>
            <ul className="space-y-3 text-sm" style={{ color: theme.colors.mutedText }}>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold">•</span>
                Be honest about the item's condition.
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold">•</span>
                Set a competitive price based on similar items.
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold">•</span>
                Use keywords in the title for better search visibility.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItem;
